import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

function useCountUp(target, active) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!active || typeof target !== "number" || !isFinite(target)) return;
    let raf;
    const dur = 900;
    const start = performance.now();
    const from = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(from + (target - from) * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
      else setDisplay(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active]);
  return display;
}

export default function StatCard({ icon: Icon, label, value, suffix, accent = "#D4AF37", testId, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const isNum = typeof value === "number" && isFinite(value);
  const hasDecimals = isNum && !Number.isInteger(value);
  const animated = useCountUp(isNum ? value : 0, inView);
  const shown = isNum
    ? (hasDecimals ? Number(animated.toFixed(1)) : Math.round(animated)).toLocaleString("en-US")
    : value;

  return (
    <motion.div
      ref={ref}
      data-testid={testId}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.4, delay }}
      whileHover={{ y: -4, transition: { type: "spring", stiffness: 320, damping: 18 } }}
      className="glass glass-hover rounded-2xl p-5 relative overflow-hidden"
    >
      <div className="absolute -top-8 -left-8 w-24 h-24 rounded-full opacity-20 blur-2xl" style={{ background: accent }} />
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm text-stone-600 font-medium">{label}</span>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${accent}22`, border: `1px solid ${accent}55` }}>
          {Icon && <Icon size={20} style={{ color: accent }} />}
        </div>
      </div>
      <div className="flex items-end gap-2">
        <span className="text-3xl font-bold font-head gold-text">{shown}</span>
        {suffix && <span className="text-xs text-stone-500 mb-1">{suffix}</span>}
      </div>
    </motion.div>
  );
}
